import React, { useState, useEffect, useCallback, useContext } from "react";
import moment from "moment";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit, Delete, WhatsApp, Add, Cancel } from "@mui/icons-material";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import HorariosModal from "./HorariosModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Espelha o backend (ProductNormalize): o banco grava "Serviço"/"Produto" em
// portugues e capitalizado. Comparar com "service" nao acha nada.
const key = (v) => (v || "").trim().toLowerCase();
const isService = (t) => ["serviço", "servico", "service"].includes(key(t));

// Expediente invalido = vazio OU sem nenhum turno com fim depois do inicio.
// Um turno "00:00-00:00" existe mas nao gera horario nenhum.
const expedienteVazio = (schedules) => {
  if (!Array.isArray(schedules) || !schedules.length) return true;
  const temTurnoValido = schedules.some((d) => {
    const okA = d.startTimeA && d.endTimeA && d.endTimeA > d.startTimeA;
    const okB = d.startTimeB && d.endTimeB && d.endTimeB > d.startTimeB;
    return okA || okB;
  });
  return !temTurnoValido;
};

const hojeISO = () => moment().format("YYYY-MM-DD");
const em30ISO = () => moment().add(30, "days").format("YYYY-MM-DD");

const Agenda = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [from, setFrom] = useState(hojeISO());
  const [to, setTo] = useState(em30ISO());
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [semHorario, setSemHorario] = useState(false);

  const [staffModal, setStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffName, setStaffName] = useState("");
  const [staffActive, setStaffActive] = useState(true);

  const [servicesModal, setServicesModal] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);

  const [horarios, setHorarios] = useState(null);

  const [confirm, setConfirm] = useState(null);

  const carregarAgendamentos = useCallback(() => {
    api
      .get("/appointments", { params: { from, to } })
      .then(({ data }) => setAppointments(data || []))
      .catch(toastError);
  }, [from, to]);

  const carregarStaff = useCallback(() => {
    api
      .get("/staff-members")
      .then(({ data }) => setStaff(data || []))
      .catch(toastError);
  }, []);

  useEffect(() => {
    carregarAgendamentos();
  }, [carregarAgendamentos]);

  useEffect(() => {
    carregarStaff();

    api
      .get("/products")
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : data?.products || [];
        setServices(lista.filter((p) => isService(p.type)));
      })
      .catch(() => setServices([]));
  }, [carregarStaff]);

  // Sem horario de funcionamento a agenda nao oferece NENHUM horario e parece
  // quebrada. Hoje quase todas as empresas estao assim.
  useEffect(() => {
    if (!user?.companyId) return;
    api
      .get(`/companies/${user.companyId}`)
      .then(({ data }) => {
        setSemHorario(expedienteVazio(data?.schedules));
      })
      .catch(() => setSemHorario(false));
  }, [user]);

  const cancelarAgendamento = (id) => {
    api
      .post(`/appointments/${id}/cancel`)
      .then(() => {
        toast.success("Agendamento cancelado.");
        carregarAgendamentos();
      })
      .catch(toastError);
  };

  const salvarStaff = () => {
    if (!staffName.trim()) {
      toast.error("Informe o nome do profissional.");
      return;
    }
    const req = editingStaff
      ? api.put(`/staff-members/${editingStaff.id}`, {
          name: staffName,
          active: staffActive,
        })
      : api.post("/staff-members", { name: staffName });

    req
      .then(() => {
        toast.success("Profissional salvo.");
        setStaffModal(false);
        setEditingStaff(null);
        setStaffName("");
        setStaffActive(true);
        carregarStaff();
      })
      .catch(toastError);
  };

  const excluirStaff = (s) => {
    api
      .delete(`/staff-members/${s.id}`)
      .then(() => {
        toast.success("Profissional excluído.");
        carregarStaff();
      })
      .catch((err) => {
        if (err?.response?.data?.error === "ERR_STAFF_HAS_APPOINTMENTS") {
          // Apagar levaria os agendamentos junto, em cascata.
          toast.error(
            "Este profissional tem agendamentos futuros. Desative-o em vez de excluir."
          );
          return;
        }
        toastError(err);
      });
  };

  const salvarServicos = () => {
    api
      .put(`/staff-members/${servicesModal.id}/services`, {
        productIds: checkedIds,
      })
      .then(() => {
        toast.success("Serviços atualizados.");
        setServicesModal(null);
        carregarStaff();
      })
      .catch(toastError);
  };

  const statusChip = (s) =>
    s === "cancelled" ? (
      <Chip size="small" label="Cancelado" />
    ) : (
      <Chip size="small" label="Confirmado" color="success" />
    );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Agenda</Title>
      </MainHeader>

      <Box sx={{ p: 2 }}>
        {semHorario && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            O <strong>expediente da empresa</strong> não define nenhum horário
            válido (vazio ou com turnos de duração zero) — enquanto isso,{" "}
            <strong>nenhum profissional</strong> recebe agendamentos, por mais
            bem configurada que esteja a grade dele. Ajuste em Configurações.
          </Alert>
        )}

        {!services.length && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Para aceitar agendamentos, cadastre um <strong>Serviço</strong> com{" "}
            <strong>duração</strong> em Produtos e Serviços, e vincule um
            profissional a ele aqui embaixo.
          </Alert>
        )}

        {/* ---------- AGENDAMENTOS ---------- */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Agendamentos</Typography>

          <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <TextField
              type="date"
              size="small"
              label="De"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              size="small"
              label="Até"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data e hora</TableCell>
                  <TableCell>Serviço</TableCell>
                  <TableCell>Profissional</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Observação</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      {moment(a.startsAt).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>{a.product?.name || "-"}</TableCell>
                    <TableCell>{a.staffMember?.name || "-"}</TableCell>
                    <TableCell>{a.customerName}</TableCell>
                    <TableCell>{a.customerPhone}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{a.notes || "-"}</TableCell>
                    <TableCell>{statusChip(a.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Abrir no WhatsApp">
                        <IconButton
                          size="small"
                          onClick={() =>
                            window.open(
                              `https://wa.me/${String(a.customerPhone).replace(
                                /\D/g,
                                ""
                              )}`,
                              "_blank"
                            )
                          }
                        >
                          <WhatsApp fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && a.status !== "cancelled" && (
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirm({
                                title: "Cancelar agendamento?",
                                message: `O horário de ${a.customerName} será liberado para outro cliente.`,
                                onConfirm: () => cancelarAgendamento(a.id),
                              })
                            }
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!appointments.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                        Nenhum agendamento neste período.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        {/* ---------- PROFISSIONAIS ---------- */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Profissionais</Typography>
            {isAdmin && (
              <Button
                size="small"
                startIcon={<Add />}
                variant="contained"
                onClick={() => {
                  setEditingStaff(null);
                  setStaffName("");
                  setStaffActive(true);
                  setStaffModal(true);
                }}
              >
                Novo
              </Button>
            )}
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Serviços que atende</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      {s.active ? (
                        <Chip size="small" label="Ativo" color="success" />
                      ) : (
                        <Chip size="small" label="Inativo" />
                      )}
                      {!s.hasSchedule && (
                        <Chip
                          size="small"
                          color="warning"
                          label="Sem horário"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setServicesModal(s);
                          setCheckedIds(s.productIds || []);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        {s.productIds?.length || 0} serviço(s)
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setHorarios(s)}
                        sx={{ textTransform: "none" }}
                      >
                        Horários
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      {isAdmin && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingStaff(s);
                              setStaffName(s.name);
                              setStaffActive(!!s.active);
                              setStaffModal(true);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirm({
                                title: "Excluir profissional?",
                                message: `${s.name} será removido.`,
                                onConfirm: () => excluirStaff(s),
                              })
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!staff.length && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                        Nenhum profissional cadastrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      {/* ---------- MODAL PROFISSIONAL ---------- */}
      <Dialog
        open={staffModal}
        onClose={() => setStaffModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {editingStaff ? "Editar profissional" : "Novo profissional"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            label="Nome"
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {editingStaff && (
            <FormControlLabel
              control={
                <Switch
                  checked={staffActive}
                  onChange={(e) => setStaffActive(e.target.checked)}
                />
              }
              label="Ativo (aceita novos agendamentos)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarStaff}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- MODAL SERVICOS DO PROFISSIONAL ---------- */}
      <Dialog
        open={!!servicesModal}
        onClose={() => setServicesModal(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Serviços que {servicesModal?.name} atende</DialogTitle>
        <DialogContent dividers>
          {services.length ? (
            services.map((p) => (
              <Box key={p.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkedIds.includes(p.id)}
                      onChange={(e) =>
                        setCheckedIds((prev) =>
                          e.target.checked
                            ? [...prev, p.id]
                            : prev.filter((x) => x !== p.id)
                        )
                      }
                    />
                  }
                  label={
                    <span>
                      {p.name}
                      {!p.durationMinutes && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 12, color: "warning.main", ml: 1 }}
                        >
                          (sem duração — não será agendável)
                        </Typography>
                      )}
                    </span>
                  }
                />
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              Nenhum serviço cadastrado.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServicesModal(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarServicos}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {horarios && (
        <HorariosModal
          open={!!horarios}
          onClose={() => setHorarios(null)}
          staff={horarios}
          onSaved={carregarStaff}
        />
      )}

      <ConfirmationModal
        title={confirm?.title || ""}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.onConfirm) confirm.onConfirm();
          setConfirm(null);
        }}
      >
        {confirm?.message}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Agenda;
